import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, AfterViewInit, OnDestroy, OnChanges, SimpleChanges, PLATFORM_ID, inject } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { TranslationService } from '../../../shared/services/translation.service';

export interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  totalItems: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

@Component({
  selector: 'app-horizontal-page-picker',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './horizontal-page-picker.component.html',
  styleUrls: ['./horizontal-page-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HorizontalPagePickerComponent implements AfterViewInit, OnDestroy, OnChanges {
  private t = inject(TranslationService);

  @Input() paginationInfo!: PaginationInfo;
  @Input() showHeader: boolean = false;
  @Input() headerTitle: string = this.t.instant('common.page');
  
  @Output() pageChange = new EventEmitter<number>();
  @Output() previousPage = new EventEmitter<void>();
  @Output() nextPage = new EventEmitter<void>();

  canScrollLeft = false;
  canScrollRight = false;
  private scrollListener?: () => void;
  private platformId = inject(PLATFORM_ID);

  // Generate all page numbers from 1 to totalPages
  getPageNumbers(): number[] {
    const totalPages = this.paginationInfo.totalPages;
    const pages: number[] = [];
    
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
    
    return pages;
  }

  // Check if a page is adjacent to the current page (for styling)
  isAdjacentPage(pageNum: number): boolean {
    const currentPage = this.paginationInfo.currentPage;
    return Math.abs(pageNum - currentPage) === 1;
  }

  onPageClick(page: number): void {
    if (page !== this.paginationInfo.currentPage) {
      this.pageChange.emit(page);
    }
  }

  onPrevClick(): void {
    if (this.paginationInfo.hasPrevPage) {
      this.previousPage.emit();
    }
  }

  onNextClick(): void {
    if (this.paginationInfo.hasNextPage) {
      this.nextPage.emit();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If the current page changed, scroll to show it
    if (changes['paginationInfo'] && !changes['paginationInfo'].firstChange) {
      const previousValue = changes['paginationInfo'].previousValue;
      const currentValue = changes['paginationInfo'].currentValue;
      
      if (previousValue?.currentPage !== currentValue?.currentPage) {
        setTimeout(() => {
          this.scrollToCurrentPage();
        }, 50);
      }
    }
  }

  private scrollToCurrentPage(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    
    const pickerWrapper = document.querySelector('.page-picker-wrapper') as HTMLElement;
    if (pickerWrapper) {
      // Calculate the position of the current page
      const currentPage = this.paginationInfo.currentPage;
      const itemWidth = 50; // Width of each page item
      const wrapperWidth = pickerWrapper.clientWidth;
      const listPadding = 60; // Left padding of .page-picker-list
      
      // Calculate the absolute position of the current page item
      const itemPosition = (currentPage - 1) * itemWidth + listPadding;
      
      // Point the current page in the wrapper
      const pointedPosition = itemPosition - (wrapperWidth / 2) + (itemWidth / 2);
      
      // Ensure we don't scroll past the boundaries
      const maxScroll = pickerWrapper.scrollWidth - wrapperWidth;
      const finalPosition = Math.max(0, Math.min(pointedPosition, maxScroll));
      
      pickerWrapper.scrollTo({
        left: finalPosition,
        behavior: 'smooth'
      });
    }
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.setupScrollListener();
      this.updateScrollButtons();
      // Scroll to show the current page
      setTimeout(() => {
        this.scrollToCurrentPage();
      }, 100);
    }
  }

  ngOnDestroy(): void {
    if (isPlatformBrowser(this.platformId) && this.scrollListener) {
      const pickerWrapper = document.querySelector('.page-picker-wrapper');
      pickerWrapper?.removeEventListener('scroll', this.scrollListener);
    }
  }

  private setupScrollListener(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    
    const pickerWrapper = document.querySelector('.page-picker-wrapper');
    if (pickerWrapper) {
      this.scrollListener = () => this.updateScrollButtons();
      pickerWrapper.addEventListener('scroll', this.scrollListener);
    }
  }

  private updateScrollButtons(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    
    const pickerWrapper = document.querySelector('.page-picker-wrapper') as HTMLElement;
    if (pickerWrapper) {
      this.canScrollLeft = pickerWrapper.scrollLeft > 0;
      this.canScrollRight = pickerWrapper.scrollLeft < (pickerWrapper.scrollWidth - pickerWrapper.clientWidth);
    }
  }
}
